import React, { PropsWithChildren, ReactNode } from "react";
import styled from "styled-components";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { AnalyticsPageName } from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface DisplayAmountProps {
  value: number;
  /** "$" for amounts; omitted for counts. */
  currency?: string;
  precision?: number;
  className?: string;
}

/**
 * The page's hero figure in the display role, drawn like the dashboard's
 * portfolio value: a smaller currency sign and muted cents.
 */
export const DisplayAmount = ({
  value,
  currency,
  precision = 2,
  className,
}: DisplayAmountProps) => {
  // Cut, not rounded, like the payout form: $99.999 is "$99.99", not "$100.00".
  const shown = toFixed(Math.abs(value), currency ? precision : 0);
  const formatted = shown.toLocaleString("en-US", {
    minimumFractionDigits: currency ? precision : 0,
    maximumFractionDigits: currency ? precision : 0,
  });
  const [whole, fraction] = formatted.split(".");
  return (
    <Display className={className}>
      {value < 0 && shown > 0 && "−"}
      {currency && <Currency>{currency}</Currency>}
      {whole}
      {fraction && <Fraction>.{fraction}</Fraction>}
    </Display>
  );
};

interface InfoLabelProps {
  tooltip?: ReactNode;
  analyticsFieldName?: string;
  analyticsPageName?: AnalyticsPageName;
}

/** A label with an optional info tooltip after it. */
export const InfoLabel = ({
  tooltip,
  analyticsFieldName,
  analyticsPageName = AnalyticsPageName.AFFILIATION,
  children,
}: PropsWithChildren<InfoLabelProps>) => (
  <Label>
    <span>{children}</span>
    {tooltip && (
      <HelperTooltip
        description={tooltip}
        analyticsPageName={analyticsPageName}
        analyticsFieldName={analyticsFieldName ?? "label"}
      />
    )}
  </Label>
);

interface ChangeMetaProps {
  value: number;
  /** The change as shown, e.g. "+$7.80". */
  formatted: ReactNode;
  suffix: ReactNode;
}

/**
 * A change in the meta line ("+6 from last month"): the change itself in the
 * direction colour (plain text, no pill), the rest muted.
 */
export const ChangeMeta = ({ value, formatted, suffix }: ChangeMetaProps) => (
  <Meta>
    <ChangeValue $value={value}>{formatted}</ChangeValue> {suffix}
  </Meta>
);

export type StatusTone = "up" | "down" | "neutral";

interface StatusChipProps {
  tone: StatusTone;
}

/**
 * A status chip: a quiet neutral chip whose dot carries the meaning (green
 * done, red declined, grey still waiting), so a column of statuses doesn't
 * turn into a column of colour.
 */
export const StatusChip = ({
  tone,
  children,
}: PropsWithChildren<StatusChipProps>) => (
  <Chip $tone={tone}>
    <span aria-hidden={true} />
    {children}
  </Chip>
);

export const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  > span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
export const Meta = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
export const FigureLarge = styled.div`
  ${newLookType.figureLarge};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
export const Figure = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const ChangeValue = styled.span<{ $value: number }>`
  font-weight: ${(p) => (p.$value ? 600 : 500)};
  color: ${(p) =>
    p.$value ? directionColor(getNewLookTokens(p.theme), p.$value) : "inherit"};
`;
const Display = styled.div`
  ${newLookType.display};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 30px;
    line-height: 36px;
  }
`;
const Currency = styled.span`
  margin-right: 1px;
  font-size: 0.72em;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Fraction = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Chip = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  ${newLookType.meta};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
  > span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => {
      const tokens = getNewLookTokens(p.theme);
      if (p.$tone === "up") return tokens.up;
      if (p.$tone === "down") return tokens.down;
      return tokens.muted;
    }};
  }
`;
